import React, { useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

interface AudioTrimmerProps {
  audioUrl: string;
  initialStart: number;
  initialEnd: number;
  onTimeChange: (start: number, end: number) => void;
  maxDuration?: number;
}

export default function AudioTrimmer({
  audioUrl,
  initialStart,
  initialEnd,
  onTimeChange,
  maxDuration = 1800,
}: AudioTrimmerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialStart);
  const [audioDuration, setAudioDuration] = useState(0);
  const [trimRange, setTrimRange] = useState([initialStart, initialEnd]);
  const [zoomRange, setZoomRange] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const MIN_GAP = 0.1;

  useEffect(() => {
    setTrimRange([initialStart, initialEnd]);
    setCurrentTime(initialStart);
    setIsZoomed(false);
  }, [initialStart, initialEnd]);

  useEffect(() => {
    setIsLoading(true);
  }, [audioUrl]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      try {
        audioRef.current.currentTime = Math.max(
          trimRange[0],
          Math.min(currentTime, trimRange[1]),
        );
        await audioRef.current.play();
      } catch {
        setIsPlaying(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const time = audioRef.current.currentTime;
    if (time < trimRange[0]) {
      // eslint-disable-next-line prefer-destructuring
      audioRef.current.currentTime = trimRange[0];
      setCurrentTime(trimRange[0]);
    } else if (time > trimRange[1]) {
      // eslint-disable-next-line prefer-destructuring
      audioRef.current.currentTime = trimRange[1];
      if (isPlaying) audioRef.current.pause();
      setCurrentTime(trimRange[1]);
    } else {
      setCurrentTime(time);
    }
  };

  const seek = (time: number) => {
    if (!audioRef.current) return;
    const t = Math.max(trimRange[0], Math.min(trimRange[1], time));
    audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const handleTrimChange = (_: Event, val: number | number[]) => {
    const range = val as number[];
    const start = Math.max(0, Math.min(range[0], audioDuration - MIN_GAP));
    const end = Math.min(audioDuration, Math.max(range[1], start + MIN_GAP));
    setTrimRange([start, end]);
    onTimeChange(start, end);
    if (audioRef.current && (currentTime < start || currentTime > end)) {
      audioRef.current.currentTime = start;
      setCurrentTime(start);
    }
  };

  const handleZoomedTrimChange = (_: Event, val: number | number[]) => {
    const range = val as number[];
    const start = Math.max(
      zoomRange[0],
      Math.min(range[0], zoomRange[1] - MIN_GAP),
    );
    const end = Math.min(zoomRange[1], Math.max(range[1], start + MIN_GAP));
    setTrimRange([start, end]);
    onTimeChange(start, end);
  };

  const zoomToTrimRange = () => {
    const pad = (trimRange[1] - trimRange[0]) * 0.2;
    setZoomRange([
      Math.max(0, trimRange[0] - pad),
      Math.min(audioDuration, trimRange[1] + pad),
    ]);
    setIsZoomed(true);
  };

  const zoomOut = () => {
    setZoomRange([0, audioDuration]);
    setIsZoomed(false);
  };

  const trimDuration = trimRange[1] - trimRange[0];

  const BAR_COUNT = 80;
  const barHeights = useRef(
    Array.from({ length: BAR_COUNT }, (_, i) =>
      Math.max(
        6,
        28 +
          Math.sin(i * 0.38) * 18 +
          Math.sin(i * 0.9) * 10 +
          Math.sin(i * 2.1) * 5,
      ),
    ),
  );

  const trimStartPct = audioDuration ? (trimRange[0] / audioDuration) * 100 : 0;
  const trimEndPct = audioDuration ? (trimRange[1] / audioDuration) * 100 : 100;
  const currentPct = audioDuration ? (currentTime / audioDuration) * 100 : 0;

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <ContentCutIcon />
        Audio Trimmer
      </Typography>

      {/* Hidden audio element */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={() => {
          if (audioRef.current) {
            const dur = audioRef.current.duration;
            setAudioDuration(dur);
            setZoomRange([0, dur]);
            audioRef.current.currentTime = initialStart;
            setIsLoading(false);
          }
        }}
        onCanPlay={() => setIsLoading(false)}
        onLoadStart={() => setIsLoading(true)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />

      {/* Waveform Visualization */}
      <Box
        sx={{
          mb: 2,
          bgcolor: "#f8fafc",
          borderRadius: 1,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          position: "relative",
          height: 140,
          cursor: isLoading ? "default" : "pointer",
        }}
        onClick={(e) => {
          if (isLoading || !audioDuration) return;
          const rect = e.currentTarget.getBoundingClientRect();
          seek(((e.clientX - rect.left) / rect.width) * audioDuration);
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <CircularProgress size={28} sx={{ color: "#3b82f6" }} />
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Loading audio...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Trim region highlight */}
            <Box
              sx={{
                position: "absolute",
                left: `${trimStartPct}%`,
                width: `${trimEndPct - trimStartPct}%`,
                top: 0,
                bottom: 0,
                backgroundColor: "rgba(59, 130, 246, 0.06)",
                borderLeft: "2px solid rgba(59,130,246,0.4)",
                borderRight: "2px solid rgba(59,130,246,0.4)",
                pointerEvents: "none",
              }}
            />

            {/* Playhead */}
            <Box
              sx={{
                position: "absolute",
                left: `${currentPct}%`,
                top: 0,
                bottom: 0,
                width: 2,
                backgroundColor: "#3b82f6",
                pointerEvents: "none",
                zIndex: 3,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: -3,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#3b82f6",
                },
              }}
            />

            {/* Mirrored bars */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                px: 1,
                gap: "2px",
              }}
            >
              {barHeights.current.map((h, i) => {
                const barPct = (i / BAR_COUNT) * 100;
                const inTrim = barPct >= trimStartPct && barPct <= trimEndPct;
                const isPast = barPct <= currentPct;

                let barColor: string;
                if (isPast && inTrim) {
                  barColor = "#3b82f6";
                } else if (inTrim) {
                  barColor = "#93c5fd";
                } else {
                  barColor = "#cbd5e1";
                }

                return (
                  <Box
                    key={i} // eslint-disable-line react/no-array-index-key
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: `${h}px`,
                        borderRadius: "2px 2px 1px 1px",
                        backgroundColor: barColor,
                        transition: "background-color 0.1s",
                      }}
                    />
                    <Box
                      sx={{
                        width: "100%",
                        height: `${h * 0.6}px`,
                        borderRadius: "1px 1px 2px 2px",
                        backgroundColor: barColor,
                        opacity: 0.45,
                        transition: "background-color 0.1s",
                      }}
                    />
                  </Box>
                );
              })}
            </Box>

            {/* Center play/pause button */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 4,
              }}
            >
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay();
                }}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: "#3b82f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  boxShadow: "0 4px 16px rgba(59,130,246,0.45)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  "&:hover": {
                    transform: "scale(1.08)",
                    boxShadow: "0 6px 20px rgba(59,130,246,0.55)",
                  },
                  "&:active": { transform: "scale(0.96)" },
                }}
              >
                {isPlaying ? (
                  <PauseIcon sx={{ color: "#fff", fontSize: 22 }} />
                ) : (
                  <PlayArrowIcon
                    sx={{ color: "#fff", fontSize: 24, ml: "2px" }}
                  />
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Controls row: Play button, time, skip controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          onClick={togglePlay}
          startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          size="small"
          disabled={isLoading}
        >
          {isPlaying ? "Pause" : "Play"}
        </Button>

        <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {formatTime(Math.max(0, currentTime - trimRange[0]))} /{" "}
          {formatTime(trimRange[1] - trimRange[0])}
        </Typography>

        <Button
          variant="text"
          size="small"
          onClick={() => seek(currentTime - 10)}
          disabled={isLoading || currentTime <= trimRange[0]}
        >
          -10s
        </Button>

        <Button
          variant="text"
          size="small"
          onClick={() => seek(trimRange[0])}
          disabled={isLoading}
        >
          Go to Start
        </Button>

        <Button
          variant="text"
          size="small"
          onClick={() => seek(trimRange[1])}
          disabled={isLoading}
        >
          Go to End
        </Button>

        <Button
          variant="text"
          size="small"
          onClick={() => seek(currentTime + 10)}
          disabled={isLoading || currentTime >= trimRange[1]}
        >
          +10s
        </Button>
      </Box>

      {/* Audio Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" gutterBottom>
          Audio Progress
        </Typography>
        <Box
          sx={{
            position: "relative",
            height: 16,
            bgcolor: "grey.300",
            borderRadius: 6,
            cursor: isLoading ? "default" : "pointer",
          }}
          onClick={(e) => {
            if (isLoading || !audioDuration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const clickPct = (e.clientX - rect.left) / rect.width;
            seek(clickPct * audioDuration);
          }}
        >
          {/* Trim range highlight */}
          <Box
            sx={{
              position: "absolute",
              left: `${trimStartPct}%`,
              width: `${trimEndPct - trimStartPct}%`,
              height: "100%",
              bgcolor: "primary.light",
              borderRadius: "inherit",
              opacity: 0.6,
              pointerEvents: "none",
            }}
          />
          {/* Current time indicator */}
          <Box
            sx={{
              position: "absolute",
              left: `${currentPct}%`,
              width: 3,
              height: "100%",
              bgcolor: "primary.main",
              borderRadius: 1.5,
              pointerEvents: "none",
              boxShadow: "0 0 4px rgba(0,0,0,0.3)",
            }}
          />
        </Box>
      </Box>

      {/* Zoom Controls */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="body2" sx={{ minWidth: "fit-content" }}>
          Zoom:{" "}
          {isZoomed
            ? `${formatTime(zoomRange[0])} – ${formatTime(zoomRange[1])}`
            : "Full View"}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Zoom to trim range">
            <span>
              <IconButton
                size="small"
                onClick={zoomToTrimRange}
                disabled={isLoading}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom out to full duration">
            <span>
              <IconButton size="small" onClick={zoomOut} disabled={!isZoomed}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Trim Range Slider */}
      <Box sx={{ mb: 2, px: 1.5 }}>
        <Typography variant="caption" gutterBottom>
          Trim Range: {formatTime(trimRange[0])} – {formatTime(trimRange[1])}{" "}
          (Duration: {formatTime(trimDuration)}
          {isZoomed ? " - Zoomed for precision" : ""})
        </Typography>
        <Slider
          value={trimRange}
          onChange={isZoomed ? handleZoomedTrimChange : handleTrimChange}
          valueLabelDisplay="auto"
          valueLabelFormat={formatTime}
          min={isZoomed ? zoomRange[0] : 0}
          max={isZoomed ? zoomRange[1] : audioDuration || 1}
          step={isZoomed ? 0.01 : 0.1}
          disabled={isLoading}
          sx={{ mt: 1 }}
          marks={[
            {
              value: isZoomed ? zoomRange[0] : 0,
              label: formatTime(isZoomed ? zoomRange[0] : 0),
            },
            {
              value: isZoomed ? zoomRange[1] : audioDuration || 1,
              label: formatTime(isZoomed ? zoomRange[1] : audioDuration),
            },
          ]}
        />
      </Box>

      {/* Start / End / Duration summary */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 2,
          textAlign: "center",
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            Start Time
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {formatTime(trimRange[0])}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            End Time
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {formatTime(trimRange[1])}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Duration
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            color={trimDuration > maxDuration ? "error.main" : "text.primary"}
          >
            {formatTime(trimDuration)}
          </Typography>
        </Box>
      </Box>

      {trimDuration > maxDuration && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Audio duration exceeds maximum allowed length of{" "}
          {formatTime(maxDuration)}
        </Alert>
      )}
    </Paper>
  );
}
